import { CropAndCropAid, LegalRestriction } from '@next/arable-lib/advice';
import { createHttpActions } from '@next/arable-lib/shared';
import { createAction, props } from '@ngrx/store';

import {
  BlightChartDetail,
  CropFieldRegistration,
  CustomerBlightRights,
  ParticipatingCropField,
  PhytophthoraPressure,
} from '../blight-v2/models/models';
import {
  BasicSpray,
  BlightRecalculationStatusPerCropField,
  CreateSprayApplicationData,
  SprayApplication,
} from '../shared/models';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';

export const setCropYear = createAction('[Crop Year] Set', props<{ cropYear: number }>());

export const cropYearChanged = createAction('[Crop Year] Changed', props<{ cropYear: number }>());

export const triggerRefreshBlightCropFields = createAction(
  '[Blight][API] Trigger that Blight Crop Fields need to be refreshed',
);

export const requestCropFieldBlightDataByCropFieldId = createAction(
  '[Blight][API] Request Blight Chart Details by Crop Field Id',
  props<{ cropFieldId: number; startDate: string; endDate: string; adviceDate: Date }>(),
);

export const [
  loadCustomerBlightRights,
  loadCustomerBlightRightsSuccess,
  loadCustomerBlightRightsFailure,
] = createHttpActions<
  void,
  { customerBlightRights: CustomerBlightRights },
  { message: string }
>('Load customer blight rights');

export const [
  loadBasicSprays,
  loadBasicSpraysSuccess,
  loadBasicSpraysFailure,
] = createHttpActions<
  { date: Date },
  { basicSprays: BasicSpray[] },
  { message: string }
>('Load basic sprays');

export const createSprayApplication = createAction(
  '[Blight][API] Create Spray Application',
  props<{ sprayApplication: CreateSprayApplicationData }>(),
);

export const [
  loadSprayApplications,
  loadSprayApplicationsSuccess,
  loadSprayApplicationsFailure,
] = createHttpActions<
  { year: number },
  { sprayApplications: SprayApplication[] },
  { message: string }
>('Load spray applications');

export const deleteSprayApplication = createAction(
  '[Blight][API] Delete Spray Application',
  props<{ sprayApplicationId: number; year: number; cropFieldId: number }>(),
);

export const requestBlightRecalculation = createAction(
  '[Blight][API] Request Blight Recalculation',
  props<{ cropFieldId: number; year: number }>(),
);

export const deleteBlightIrrigation = createAction(
  '[Blight][API] Delete Blight Irrigation',
  props<{ irrigationId: number; cropFieldId: number; year: number }>(),
);

export const loadCropFieldRegistration = createAction(
  '[Blight][API] Load Crop Field Registration',
  props<{ cropFieldId: number; year: number }>(),
);

export const updatePlantDate = createAction(
  '[Blight][API] Update Plant Date',
  props<{ date: Date; cropFieldId: number; year: number }>(),
);

export const updateEmergenceDate = createAction(
  '[Blight][API] Update Emergence Date',
  props<{ date: Date; cropFieldId: number; year: number }>(),
);

export const updateHarvestDate = createAction(
  '[Blight][API] Update Harvest Date',
  props<{ date: Date; cropFieldId: number; year: number }>(),
);

export const updateSuppressSprayAdvice = createAction(
  '[Blight][API] Update Suppress Spray Advice',
  props<{ suppressSprayAdvice: boolean; cropFieldId: number; year: number }>(),
);

export const updatePhytophthoraPressure = createAction(
  '[Blight][API] Update PhytophthoraPressure Date',
  props<{ phytophthoraPressure: PhytophthoraPressure; cropFieldId: number; year: number }>(),
);

export const updateCropFieldVisibilityStatus = createAction(
  '[Blight][API] Update Crop Field Visibility Status',
  props<{ cropFieldId: number; isVisible: boolean }>(),
);

export const updateCropFieldVisibilityStatusSuccess = createAction(
  '[Blight][API] Update Crop Field Visibility Status Success',
  props<{ cropFieldId: number; isVisible: boolean }>(),
);

export const setShouldGetNewBlightData = createAction(
  '[Blight][UI] Update Crop Field Visibility Status Success',
  props<{ shouldGetNewBlightData: boolean }>(),
);

export const setBlightAdviceDate = createAction(
  '[Blight] Set Blight Advice date',
  props<{ adviceDate: Date }>(),
);

export const setBlightAdviceDateOneDayBack = createAction(
  '[Blight] Set Blight Advice date one day back',
);

export const setBlightAdviceDateOneDayForward = createAction(
  '[Blight] Set Blight Advice date one day forward',
);

export const setBlightChartDetails = createAction(
  '[Blight] Set Blight Chart Details',
  props<{ blightChartDetails: BlightChartDetail[] }>(),
);

export const setNewBlightDataForCropField = createAction(
  '[Blight] Set value that new blight data is available for a cropfield',
  props<{ cropFieldId: number; timestamp: Date }>(),
);

export const openSpraysDialog = createAction(
  '[Blight][UI] Open sprays dialog',
  props<{ sprayType: number; advices: CropFieldAdvice[]; actionDate?: Date; readOnly?: boolean }>(),
);

export const openBlightIrrigation = createAction(
  '[Blight][UI] Open blight irrigation',
  props<{ advices: CropFieldAdvice[], actionDate?: Date }>(),
);

export const sprayApplicationCreated = createAction(
  '[Blight][API] Spray Application Created',
);

export const setIsSpraysLoading = createAction(
  '[Blight][UI] Set Spray Applications Loading',
  props<{ isSpraysLoading: boolean }>(),
);

export const setBlightRecalculationStatusPerCropField = createAction(
  '[Blight][UI] Set Blight Recalculation Status by crop field',
  props<{ blightRecalculationStatusPerCropField: BlightRecalculationStatusPerCropField }>(),
);

export const setCropFieldRegistration = createAction(
  '[Blight][UI] Set Crop Field Registration',
  props<{ cropFieldRegistration: CropFieldRegistration }>(),
);

export const addShouldRecalculate = createAction(
  '[Blight][UI] Add Should Recalculate',
  props<{ cropFieldId: number }>(),
);

export const removeShouldRecalculate = createAction(
  '[Blight][UI] Remove Should Recalculate',
  props<{ cropFieldId: number }>(),
);

export const setShowRecalculationFinishedNotification = createAction(
  '[Blight][UI] Set ShowRecalculationFinishedNotification',
  props<{ value: boolean }>(),
);

export const setSelectedCropField = createAction(
  '[Blight][UI] Set Selected Crop Field',
  props<{ cropField: ParticipatingCropField }>(),
);

export const setSelectedBlightCropFieldIds = createAction(
  '[Blight][UI] Set selected blight crop field ids',
  props<{ cropFieldIds: number[] }>(),
);

export const removeSelectedBlightCropField = createAction(
  '[Blight][UI] Remove selected blight crop field',
  props<{ cropFieldId: number }>(),
);

export const plantDateUpdated = createAction(
  '[Blight][API] Plant Date Updated',
  props<{ cropFieldRegistration: CropFieldRegistration }>(),
);

export const emergenceDateUpdated = createAction(
  '[Blight][API] Emergence Date Updated',
  props<{ cropFieldRegistration: CropFieldRegistration }>(),
);

export const harvestDateUpdated = createAction(
  '[Blight][API] Harvest Date Updated',
  props<{ cropFieldRegistration: CropFieldRegistration }>(),
);

export const [
  loadLegalRestrictions,
  loadLegalRestrictionsSuccess,
  loadLegalRestrictionsFailure,
] = createHttpActions<
  { cropAndCropAids: CropAndCropAid[] },
  { legalRestrictions: LegalRestriction[] },
  { message: string }
>('Load legal restrictions');

export const loadCropFieldsForSpray = createAction(
  '[Spray][CropFields] Load crop fields for spray',
  props<{ date: string }>(),
);

export const setCropFieldsForSpray = createAction(
  '[Spray][CropFields] Set crop fields for spray',
  props<{ cropFieldIds: number[] }>(),
);

// Unsaved Changes Guard Actions
export const checkUnsavedChanges = createAction(
  '[Guard] Check unsaved changes',
  props<{ targetUrl: string }>(),
);

export const confirmLeaveWithUnsavedChanges = createAction(
  '[Guard] Confirm leave with unsaved changes',
  props<{ targetUrl: string }>(),
);

export const proceedToTargetUrl = createAction(
  '[Guard] Proceed to target url',
  props<{ targetUrl: string }>(),
);
